/**
 * QuoteDetailController — page /devis-detail.html (EPIC-012 / 12.6, refonte
 * EPIC-051 / 51.5).
 *
 * Charge un devis depuis `GET /quotes/{id}` (payload enrichi par 12.2 :
 * events + linkedInvoices + isLocked + paymentProvider.{linkOpenedAt, paidAt})
 * puis le lot du wizard de facturation §7.G via
 * `GET /quotes/{id}/invoices`. Affiche la section unifiée « Factures
 * liées à ce devis » et expose trois boutons contextuels :
 *   - « Générer les factures »          → ouvre le wizard si aucune facture
 *   - « Recommencer le découpage »      → DELETE /quotes/{id}/invoices/drafts
 *                                          (tant qu'aucune facture émise)
 *   - « Ajouter une facture de situation » → redirection
 *                                          `/facture-edition.html?quoteId=…&kind=milestone`
 *                                          (dès qu'au moins une émise et settlement non émis)
 *
 * Vocabulaire UI strict (cf. CLAUDE.md) : « service d'encaissement », « PA »,
 * jamais Wero / Sweego / PDP / NotaFacto dans le DOM rendu.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const STATUS_LABELS = {
  draft: "brouillon",
  issued: "émis — non envoyé",
  sent: "envoyé — en attente de réponse",
  accepted: "accepté",
  refused: "refusé",
  partially_invoiced: "partiellement facturé",
  fully_invoiced: "facturé",
  expired: "expiré",
  archived: "archivé",
};

const STATUS_PILL = {
  draft: "nm-pill--indigo",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  accepted: "nm-pill--olive",
  refused: "nm-pill--red",
  partially_invoiced: "nm-pill--blue",
  fully_invoiced: "nm-pill--blue",
  expired: "",
  archived: "",
};

const INVOICE_STATUS_LABELS = {
  draft: "brouillon",
  issued: "émis",
  sent: "envoyé",
  paid: "payé",
  partially_paid: "partiellement payé",
  overdue: "en retard",
  cancelled: "annulé",
  archived: "archivé",
};

const INVOICE_STATUS_PILL = {
  draft: "nm-pill--indigo",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  paid: "nm-pill--olive",
  partially_paid: "nm-pill--blue",
  overdue: "nm-pill--red",
  cancelled: "",
  archived: "",
};

const INVOICE_KIND_LABELS = {
  standard: "Facture standard",
  advance_payment: "Acompte",
  milestone: "Jalon / situation",
  settlement: "Solde",
  credit_note: "Avoir",
};

const KIND_ORDER = {
  advance_payment: 1,
  milestone: 2,
  settlement: 3,
  standard: 4,
  credit_note: 5,
};

const EVENT_LABELS = {
  draft_created: "Brouillon créé",
  issued: "Devis émis",
  sent: "Devis envoyé au client",
  accepted: "Devis accepté",
  refused: "Devis refusé",
  partially_invoiced: "Devis partiellement facturé",
  fully_invoiced: "Devis facturé en intégralité",
  expired: "Devis expiré",
  archived: "Devis archivé",
  payment_provider_link_opened: "Lien du service d'encaissement ouvert",
  accepted_via_payment_provider: "Acompte payé — devis accepté",
  accepted_via_public_page: "Accepté via la page client",
  refused_via_public_page: "Refusé via la page client",
  renewal_requested: "Le client a demandé un devis à jour",
  validity_extended: "Validité prolongée",
  advance_payment_invoice_issued: "Facture d'acompte émise",
  milestones_defined: "Jalons définis",
  milestones_reset: "Jalons réinitialisés",
  fully_invoiced_via_conversion: "Devis converti en facture en 1 clic",
};

// La page publique du devis est servie par l'app elle-même
// (PublicQuoteController::showPage(), route GET /devis/{token}), donc même origine
// que cette page : on dérive l'URL de window.location.origin pour fonctionner en
// local comme en prod (cf. décision PO 2026-06-02 : documents émis sur l'app).
const PUBLIC_LINK_PREFIX = `${window.location.origin}/devis/`;

export default class QuoteDetailController extends BaseController {
  static targets = [
    "crumbNumber",
    "overline",
    "title",
    "statusPill",
    "statusLabel",
    "importedBadge",
    "dateCreated",
    "dateValidity",
    "lockedBanner",
    "actionsBar",
    "duplicateBtn",
    "editLink",
    "createComplementBtn",
    "childQuotesCard",
    "childQuotesBody",
    "linesBody",
    "paymentProviderCard",
    "paymentProviderPill",
    "paymentProviderStatus",
    "paymentProviderInfo",
    "paymentProviderAmount",
    "linkedInvoicesCard",
    "linkedInvoicesBody",
    "invoicesCounter",
    "openWizardBtn",
    "resetDraftsBtn",
    "addMilestoneBtn",
    "addMilestoneForm",
    "addMilestoneLabel",
    "addMilestonePercent",
    "addMilestoneAmount",
    "addMilestoneError",
    "addMilestoneSubmit",
    "invoicesActionError",
    "timeline",
    "clientLink",
    "clientName",
    "clientAddress",
    "totalHt",
    "totalTtc",
    "grossHtRow",
    "grossHt",
    "discountRow",
    "discountPercent",
    "discountLabel",
    "discountAmount",
    "renewalAlert",
    "renewalMessage",
    "renewalDate",
    "sendBtn",
    "extendBtn",
    "renewBtn",
    "extendPanel",
    "extendDateInput",
    "extendError",
    "extendSubmitBtn",
    "renewPanel",
    "renewError",
    "renewSubmitBtn",
    "publicLinkCard",
    "publicLinkUrl",
    "publicLinkCopyBtn",
  ];

  connect() {
    this._quoteId = new URLSearchParams(window.location.search).get("id");
    if (!this._quoteId) {
      this.showError("Identifiant devis manquant dans l'URL.");
      return;
    }
    this.refresh();
  }

  async refresh() {
    try {
      const [quote, invoicesResp] = await Promise.all([
        api.getQuote(this._quoteId),
        api.getQuoteInvoices(this._quoteId).catch(() => ({ invoices: [] })),
      ]);
      this._quote = quote;
      const invoices = Array.isArray(invoicesResp?.invoices) ? invoicesResp.invoices : [];
      this._invoices = invoices;
      this._renderHead(quote);
      this._renderLines(quote);
      this._renderTotals(quote);
      this._renderPaymentProvider(quote);
      this._renderQuoteInvoices(quote, invoices);
      this._renderChildQuotes(quote);
      this._renderTimeline(quote);
      this._renderRenewalAlert(quote);
      this._renderPublicLink(quote);
      this._renderActions(quote);
      await this._hydrateClient(quote.clientId);
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger le devis.";
      this.showError(msg);
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  _renderHead(quote) {
    const number = quote.number ?? "Brouillon";
    if (this.hasCrumbNumberTarget) this.crumbNumberTarget.textContent = number;
    if (this.hasTitleTarget) this.titleTarget.textContent = number;
    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent = quote.status === "draft" ? "Brouillon" : "Devis émis";
    }
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = STATUS_LABELS[quote.status] ?? quote.status;
    }
    if (this.hasStatusPillTarget) {
      this.statusPillTarget.className = "nm-pill";
      const modifier = STATUS_PILL[quote.status];
      if (modifier) this.statusPillTarget.classList.add(modifier);
    }
    if (this.hasImportedBadgeTarget) {
      this.importedBadgeTarget.hidden = quote.origin !== "imported";
    }
    if (this.hasDateCreatedTarget) {
      this.dateCreatedTarget.textContent = quote.dateCreated ? this.formatDate(quote.dateCreated) : "—";
      if (quote.dateCreated) this.dateCreatedTarget.setAttribute("datetime", quote.dateCreated);
    }
    if (this.hasDateValidityTarget) {
      this.dateValidityTarget.textContent = quote.dateValidity ? this.formatDate(quote.dateValidity) : "—";
      if (quote.dateValidity) this.dateValidityTarget.setAttribute("datetime", quote.dateValidity);
    }
    if (this.hasLockedBannerTarget) {
      this.lockedBannerTarget.hidden = !quote.isLocked;
    }
    if (this.hasEditLinkTarget) {
      this.editLinkTarget.setAttribute(
        "href",
        `/devis-edition.html?id=${encodeURIComponent(quote.id)}`,
      );
    }
  }

  _renderLines(quote) {
    if (!this.hasLinesBodyTarget) return;
    while (this.linesBodyTarget.firstChild) {
      this.linesBodyTarget.removeChild(this.linesBodyTarget.firstChild);
    }
    const lines = Array.isArray(quote.lines) ? quote.lines : [];
    if (lines.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 5;
      td.className = "is-muted";
      td.style.padding = "16px";
      td.style.textAlign = "center";
      td.textContent = "Aucune ligne sur ce devis.";
      tr.appendChild(td);
      this.linesBodyTarget.appendChild(tr);
      return;
    }
    for (const line of lines) {
      const fragment = this.cloneTemplate("tpl-quote-line-row");
      const quantity = Number(line.quantity);
      const priceExclVat = Number(line.priceExclVat);
      const lineTotal = Number.isFinite(quantity) && Number.isFinite(priceExclVat)
        ? quantity * priceExclVat
        : null;
      this.fillSlots(fragment, {
        label: line.label,
        reference: line.reference ?? "—",
        quantity: Number.isFinite(quantity) ? quantity.toLocaleString("fr-FR") : line.quantity,
        priceExclVat: this._formatMoney(line.priceExclVat),
        lineTotal: lineTotal !== null ? this._formatMoney(lineTotal) : "—",
      });
      this.linesBodyTarget.appendChild(fragment);
    }
  }

  _renderTotals(quote) {
    const hasDiscount = Number(quote.discountPercent) > 0;

    // Total HT (net après remise) et Total TTC (net) — toujours affichés. Sans
    // remise, amountNet* === amount* (la colonne générée vaut le brut).
    if (this.hasTotalHtTarget) {
      this.totalHtTarget.textContent = `${this._formatMoney(quote.amountNetExclVat)} €`;
    }
    if (this.hasTotalTtcTarget) {
      this.totalTtcTarget.textContent = `${this._formatMoney(quote.amountNetInclVat)} €`;
    }

    // Lignes « Total HT avant remise » + « Remise » : uniquement s'il y a une
    // remise, insérées avant le Total HT net.
    if (this.hasGrossHtRowTarget) this.grossHtRowTarget.hidden = !hasDiscount;
    if (this.hasDiscountRowTarget) this.discountRowTarget.hidden = !hasDiscount;
    if (!hasDiscount) return;

    if (this.hasGrossHtTarget) {
      this.grossHtTarget.textContent = `${this._formatMoney(quote.amountExclVat)} €`;
    }
    if (this.hasDiscountPercentTarget) {
      this.discountPercentTarget.textContent = this._formatMoney(quote.discountPercent);
    }
    if (this.hasDiscountLabelTarget) {
      this.discountLabelTarget.textContent = quote.discountLabel ?? "";
    }
    if (this.hasDiscountAmountTarget) {
      // Montant remisé = brut − net (cohérent avec la colonne générée STORED,
      // pas de recalcul par pourcentage qui réintroduirait un arrondi).
      const gross = Number(quote.amountExclVat);
      const net = Number(quote.amountNetExclVat);
      const discount = Number.isFinite(gross) && Number.isFinite(net) ? gross - net : null;
      this.discountAmountTarget.textContent =
        discount === null ? "—" : `−${this._formatMoney(discount)} €`;
    }
  }

  _renderPaymentProvider(quote) {
    if (!this.hasPaymentProviderCardTarget) return;
    const pp = quote.paymentProvider ?? {};
    const opened = pp.linkOpenedAt;
    const paid = pp.paidAt;
    const enabled = !!pp.enabled;
    if (!enabled && !opened && !paid) {
      this.paymentProviderCardTarget.hidden = true;
      return;
    }
    this.paymentProviderCardTarget.hidden = false;
    let state = "non payé";
    let modifier = "";
    if (paid) {
      state = "payé";
      modifier = "nm-pill--olive";
    } else if (opened) {
      state = "lien ouvert";
      modifier = "nm-pill--ochre";
    } else {
      state = "en attente";
      modifier = "nm-pill--indigo";
    }
    if (this.hasPaymentProviderStatusTarget) {
      this.paymentProviderStatusTarget.textContent = state;
    }
    if (this.hasPaymentProviderPillTarget) {
      this.paymentProviderPillTarget.className = "nm-pill";
      if (modifier) this.paymentProviderPillTarget.classList.add(modifier);
    }
    if (this.hasPaymentProviderInfoTarget) {
      const parts = [];
      if (pp.depositPercentage) parts.push(`Acompte ${this._formatMoney(pp.depositPercentage)} %`);
      if (paid) parts.push(`payé le ${this.formatDate(paid)}`);
      else if (opened) parts.push(`lien ouvert le ${this.formatDate(opened)}`);
      this.paymentProviderInfoTarget.textContent = parts.join(" · ") || "—";
    }
    if (this.hasPaymentProviderAmountTarget) {
      if (pp.depositPercentage && quote.amountNetInclVat) {
        const ttc = Number(quote.amountNetInclVat);
        const pct = Number(pp.depositPercentage);
        if (Number.isFinite(ttc) && Number.isFinite(pct)) {
          this.paymentProviderAmountTarget.textContent = `${this._formatMoney((ttc * pct) / 100)} €`;
        }
      } else {
        this.paymentProviderAmountTarget.textContent = "—";
      }
    }
  }

  _renderQuoteInvoices(quote, invoices) {
    if (!this.hasLinkedInvoicesBodyTarget) return;
    while (this.linkedInvoicesBodyTarget.firstChild) {
      this.linkedInvoicesBodyTarget.removeChild(this.linkedInvoicesBodyTarget.firstChild);
    }
    const live = invoices.filter((i) => i.status !== "archived");
    const sorted = live.slice().sort((a, b) => this._sortInvoices(a, b));
    if (sorted.length === 0) {
      const tr = document.createElement("tr");
      const td = document.createElement("td");
      td.colSpan = 5;
      td.className = "is-muted";
      td.style.padding = "16px";
      td.style.textAlign = "center";
      td.textContent = "Aucune facture liée à ce devis pour l'instant.";
      tr.appendChild(td);
      this.linkedInvoicesBodyTarget.appendChild(tr);
    } else {
      for (const inv of sorted) {
        const fragment = this.cloneTemplate("tpl-quote-invoice-row");
        const status = inv.status ?? "draft";
        const labelText = this._invoiceLabel(inv);
        const kindLabel = this._invoiceKindLabel(inv);
        const isSettlement = inv.kind === "settlement";
        const isSettlementDraft = isSettlement && status === "draft";
        this.fillSlots(fragment, {
          label: labelText,
          kind: kindLabel,
          amount: isSettlementDraft
            ? ""
            : (inv.amountInclVat ? `${this._formatMoney(inv.amountInclVat)} €` : "—"),
          statusLabel: INVOICE_STATUS_LABELS[status] ?? status,
          date: inv.dateEmission ? this.formatDate(inv.dateEmission) : "—",
        });
        if (isSettlementDraft) this._appendSettlementAutoPill(fragment, inv);
        const link = fragment.querySelector('[data-slot-element="link"]');
        if (link) {
          if (isSettlementDraft) {
            // EPIC-099 / 99.49 — D6 : brouillon de solde non cliquable
            // (non éditable, recalculé à la volée). On remplace l'<a> par
            // un <span> pour ne pas donner d'affordance.
            const span = document.createElement("span");
            while (link.firstChild) span.appendChild(link.firstChild);
            link.replaceWith(span);
          } else if (inv.id) {
            link.setAttribute("href", `/facture-detail.html?id=${encodeURIComponent(inv.id)}`);
          }
        }
        const dateEl = fragment.querySelector('[data-slot-attr-datetime="dateIso"]');
        if (dateEl && inv.dateEmission) dateEl.setAttribute("datetime", inv.dateEmission);
        const pill = fragment.querySelector('[data-slot-element="statusPill"]');
        const pillMod = INVOICE_STATUS_PILL[status];
        if (pill && pillMod) pill.classList.add(pillMod);
        this.linkedInvoicesBodyTarget.appendChild(fragment);
      }
    }

    const billed = live
      .filter((i) => i.status !== "draft" && i.status !== "cancelled" && i.kind !== "credit_note" && i.kind !== "settlement")
      .reduce((s, i) => s + (Number(i.amountInclVat) || 0), 0);
    const total = Number(quote.amountNetInclVat) || 0;
    if (this.hasInvoicesCounterTarget) {
      this.invoicesCounterTarget.textContent =
        `${this._formatMoney(billed)} € facturé sur ${this._formatMoney(total)} €`;
    }

    this._renderQuoteInvoicesButtons(quote, live);
  }

  _invoiceLabel(inv) {
    if (inv.number) return inv.number;
    return inv.milestoneLabel || "—";
  }

  _invoiceKindLabel(inv) {
    if (inv.kind === "milestone" && inv.milestoneIndex && inv.milestoneTotal) {
      return `Jalon ${inv.milestoneIndex}/${inv.milestoneTotal}`;
    }
    return INVOICE_KIND_LABELS[inv.kind] ?? inv.kind ?? "—";
  }

  _sortInvoices(a, b) {
    const aIssued = a.dateEmission ? Date.parse(a.dateEmission) : Number.POSITIVE_INFINITY;
    const bIssued = b.dateEmission ? Date.parse(b.dateEmission) : Number.POSITIVE_INFINITY;
    if (aIssued !== bIssued) return aIssued - bIssued;
    const aKind = KIND_ORDER[a.kind] ?? 99;
    const bKind = KIND_ORDER[b.kind] ?? 99;
    if (aKind !== bKind) return aKind - bKind;
    return (a.milestoneIndex ?? 0) - (b.milestoneIndex ?? 0);
  }

  _renderQuoteInvoicesButtons(quote, invoices) {
    const status = quote.status;
    const editable = status === "accepted" && !quote.isLocked;
    const live = invoices.filter((i) => i.status !== "cancelled");
    const hasAny = live.length > 0;
    const hasIssued = live.some((i) => i.status !== "draft");
    const settlementIssued = live.some(
      (i) => i.kind === "settlement" && i.status !== "draft" && i.status !== "cancelled",
    );

    if (this.hasOpenWizardBtnTarget) {
      this.openWizardBtnTarget.hidden = !editable || hasAny;
      this.openWizardBtnTarget.setAttribute(
        "href",
        `/devis-facturer-edition.html?quoteId=${encodeURIComponent(this._quoteId)}`,
      );
    }
    if (this.hasResetDraftsBtnTarget) {
      this.resetDraftsBtnTarget.hidden = !editable || !hasAny || hasIssued;
    }
    if (this.hasAddMilestoneBtnTarget) {
      // Bouton visible dès qu'au moins une facture (draft ou émise) existe
      // tant que le settlement n'est pas émis. EPIC-099 / 99.49 — D5 : ouvre
      // un mini-formulaire inline (pas de page dédiée). Découplé de `editable`
      // (qui couvre la modification du devis) : `isLocked = true` dès la 1re
      // facture créée — c'est précisément l'état où l'on veut pouvoir ajouter
      // un jalon.
      const formOpen = this.hasAddMilestoneFormTarget && !this.addMilestoneFormTarget.hidden;
      const lifecycleOk = status === "accepted" || status === "partially_invoiced";
      this.addMilestoneBtnTarget.hidden = !lifecycleOk || !hasAny || settlementIssued || formOpen;
    }
  }

  _renderTimeline(quote) {
    if (!this.hasTimelineTarget) return;
    const events = Array.isArray(quote.events) ? quote.events : [];
    while (this.timelineTarget.firstChild) {
      this.timelineTarget.removeChild(this.timelineTarget.firstChild);
    }
    if (events.length === 0) {
      const li = document.createElement("li");
      li.className = "is-muted fs-13";
      li.style.display = "block";
      li.textContent = "Aucun événement enregistré.";
      this.timelineTarget.appendChild(li);
      return;
    }
    for (const ev of events.slice(0, 10)) {
      const fragment = this.cloneTemplate("tpl-timeline-event");
      const happenedAt = ev.happenedAt ?? ev.happened_at ?? null;
      const eventName = ev.event ?? "";
      const baseLabel = EVENT_LABELS[eventName] ?? eventName;
      const detail = this._eventDetail(eventName, ev.data ?? {});
      this.fillSlots(fragment, {
        happenedAt: happenedAt ? this.formatDate(happenedAt, { includeTime: true }) : "—",
        label: baseLabel,
        detail: detail ?? "",
      });
      const timeEl = fragment.querySelector('[data-slot-attr-datetime="happenedAtIso"]');
      if (timeEl && happenedAt) timeEl.setAttribute("datetime", happenedAt);
      const detailEl = fragment.querySelector('[data-slot="detail"]');
      if (detailEl && !detail) detailEl.hidden = true;
      this.timelineTarget.appendChild(fragment);
    }
  }

  _eventDetail(eventName, data) {
    if (!data || typeof data !== "object") return null;
    switch (eventName) {
      case "accepted_via_public_page":
      case "refused_via_public_page":
        return data.signatory_name ? `Signataire : ${data.signatory_name}` : null;
      case "renewal_requested":
        return data.message ? `« ${String(data.message).trim()} »` : null;
      case "validity_extended":
        return data.new_validity_date
          ? `Nouvelle date de validité : ${this.formatDate(data.new_validity_date)}`
          : null;
      default:
        return null;
    }
  }

  _renderActions(quote) {
    const status = quote.status;
    const locked = !!quote.isLocked;
    const editable = status === "draft" && !locked;

    if (this.hasEditLinkTarget) {
      if (editable) {
        this.editLinkTarget.style.pointerEvents = "";
        this.editLinkTarget.removeAttribute("aria-disabled");
      } else {
        this.editLinkTarget.setAttribute("aria-disabled", "true");
        this.editLinkTarget.style.pointerEvents = "none";
        this.editLinkTarget.style.opacity = "0.5";
      }
    }
    if (this.hasDuplicateBtnTarget) {
      this.duplicateBtnTarget.disabled = false;
    }
    if (this.hasCreateComplementBtnTarget) {
      const terminalNegative = status === "archived" || status === "refused" || status === "expired";
      this.createComplementBtnTarget.hidden = !locked || terminalNegative;
    }
    if (this.hasSendBtnTarget) {
      const showAsResend = status === "sent" || status === "expired";
      const showAsInitial = status === "issued";
      this.sendBtnTarget.hidden = !(showAsResend || showAsInitial);
      this.sendBtnTarget.textContent = showAsResend
        ? "Renvoyer au client"
        : "Envoyer au client";
    }
    if (this.hasExtendBtnTarget) {
      this.extendBtnTarget.hidden = !(status === "sent" || status === "expired");
    }
    if (this.hasRenewBtnTarget) {
      this.renewBtnTarget.hidden = !(status === "expired" || status === "refused");
    }
  }

  _renderRenewalAlert(quote) {
    if (!this.hasRenewalAlertTarget) return;
    const events = Array.isArray(quote.events) ? quote.events : [];
    const renewal = events.find((e) => (e.event ?? "") === "renewal_requested");
    if (!renewal) {
      this.renewalAlertTarget.hidden = true;
      return;
    }
    this.renewalAlertTarget.hidden = false;
    const data = renewal.data ?? {};
    if (this.hasRenewalMessageTarget) {
      const msg = data.message ? String(data.message).trim() : "";
      this.renewalMessageTarget.textContent = msg ? `« ${msg} »` : "";
      this.renewalMessageTarget.hidden = !msg;
    }
    if (this.hasRenewalDateTarget) {
      const at = renewal.happenedAt ?? renewal.happened_at ?? null;
      this.renewalDateTarget.textContent = at ? this.formatDate(at, { includeTime: true }) : "—";
      if (at) this.renewalDateTarget.setAttribute("datetime", at);
    }
  }

  _renderPublicLink(quote) {
    if (!this.hasPublicLinkCardTarget) return;
    const token = quote.publicToken ?? null;
    const status = quote.status;
    const hidden = !token || status === "draft" || status === "archived";
    this.publicLinkCardTarget.hidden = hidden;
    if (hidden) return;
    const url = `${PUBLIC_LINK_PREFIX}${token}`;
    if (this.hasPublicLinkUrlTarget) {
      this.publicLinkUrlTarget.textContent = url;
      this.publicLinkUrlTarget.setAttribute("href", url);
    }
  }

  _renderChildQuotes(quote) {
    if (!this.hasChildQuotesCardTarget || !this.hasChildQuotesBodyTarget) return;
    const children = Array.isArray(quote.childQuotes) ? quote.childQuotes : [];
    while (this.childQuotesBodyTarget.firstChild) {
      this.childQuotesBodyTarget.removeChild(this.childQuotesBodyTarget.firstChild);
    }
    if (children.length === 0) {
      this.childQuotesCardTarget.hidden = true;
      return;
    }
    this.childQuotesCardTarget.hidden = false;
    for (const child of children) {
      const fragment = this.cloneTemplate("tpl-quote-child-row");
      const status = child.status ?? "draft";
      this.fillSlots(fragment, {
        number: child.number ?? "—",
        issuedAt: child.issuedAt ? this.formatDate(child.issuedAt) : "—",
        statusLabel: STATUS_LABELS[status] ?? status,
        totalTtc: child.totalTtc !== null && child.totalTtc !== undefined
          ? `${this._formatMoney(child.totalTtc)} €`
          : "—",
      });
      const dateEl = fragment.querySelector('[data-slot-attr-datetime="issuedAtIso"]');
      if (dateEl && child.issuedAt) dateEl.setAttribute("datetime", child.issuedAt);
      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const pillMod = STATUS_PILL[status];
      if (pill && pillMod) pill.classList.add(pillMod);
      const href = child.id
        ? `/devis-detail.html?id=${encodeURIComponent(child.id)}`
        : "#";
      const link = fragment.querySelector('[data-slot-element="link"]');
      if (link) link.setAttribute("href", href);
      const detailLink = fragment.querySelector('[data-slot-element="detailLink"]');
      if (detailLink) detailLink.setAttribute("href", href);
      this.childQuotesBodyTarget.appendChild(fragment);
    }
  }

  openCreateComplement() {
    if (!this._quoteId) return;
    window.location.assign(
      `/devis-edition.html?parent=${encodeURIComponent(this._quoteId)}`,
    );
  }

  async _hydrateClient(clientId) {
    if (!clientId) return;
    try {
      const client = await api.getClient(clientId);
      if (this.hasClientLinkTarget) {
        this.clientLinkTarget.setAttribute(
          "href",
          `/clients-detail.html?id=${encodeURIComponent(clientId)}`,
        );
      }
      if (this.hasClientNameTarget) {
        this.clientNameTarget.textContent = client.legalName ?? "—";
      }
      if (this.hasClientAddressTarget) {
        this._renderClientAddress(client);
      }
    } catch {
      if (this.hasClientNameTarget) this.clientNameTarget.textContent = "(client introuvable)";
    }
  }

  _renderClientAddress(client) {
    while (this.clientAddressTarget.firstChild) {
      this.clientAddressTarget.removeChild(this.clientAddressTarget.firstChild);
    }
    const addresses = Array.isArray(client.addresses) ? client.addresses : [];
    const billing = addresses.find((a) => a.type === "billing") ?? addresses[0];
    if (billing) {
      this._appendLine(billing.line1);
      if (billing.line2) this._appendLine(billing.line2);
      this._appendLine([billing.postalCode, billing.city].filter(Boolean).join(" "));
    }
    if (client.siret) {
      const span = document.createElement("span");
      span.className = "is-mono";
      span.textContent = `SIRET ${client.siret}`;
      this.clientAddressTarget.appendChild(span);
      this.clientAddressTarget.appendChild(document.createElement("br"));
    }
    const contacts = Array.isArray(client.contacts) ? client.contacts : [];
    const primary = contacts.find((c) => c.isPrimary) ?? contacts[0];
    if (primary && primary.email) {
      const a = document.createElement("a");
      a.href = `mailto:${primary.email}`;
      a.textContent = primary.email;
      a.style.textDecoration = "underline";
      a.style.marginTop = "6px";
      a.style.display = "inline-block";
      this.clientAddressTarget.appendChild(a);
    }
  }

  _appendLine(text) {
    if (!text) return;
    this.clientAddressTarget.appendChild(document.createTextNode(text));
    this.clientAddressTarget.appendChild(document.createElement("br"));
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async downloadPdf() {
    if (!this._quoteId) return;
    const url = api.quotePreviewPdfUrl(this._quoteId);
    window.open(url, "_blank", "noopener");
  }

  duplicate() {
    this.showError("Duplication disponible depuis le wizard de création (à venir).");
  }

  _appendSettlementAutoPill(fragment) {
    const cell = fragment.querySelector('[data-slot="amount"]');
    if (!cell) return;
    const pill = document.createElement("span");
    pill.className = "nm-pill";
    pill.title = "Montant calculé à l'émission, une fois tous les jalons émis";
    pill.setAttribute("aria-label", "Montant calculé automatiquement à l'émission");
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    dot.setAttribute("aria-hidden", "true");
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode("auto"));
    cell.appendChild(pill);
  }

  // ---------------------------------------------------------------------------
  // EPIC-099 / 99.49 — D5 : Mini-form inline d'ajout d'un jalon post-création.
  // ---------------------------------------------------------------------------

  openAddMilestoneForm(event) {
    if (event) event.preventDefault();
    if (!this.hasAddMilestoneFormTarget) return;
    this.addMilestoneFormTarget.hidden = false;
    if (this.hasAddMilestoneBtnTarget) this.addMilestoneBtnTarget.hidden = true;
    if (this.hasInvoicesActionErrorTarget) this.invoicesActionErrorTarget.hidden = true;
    if (this.hasAddMilestoneLabelTarget) this.addMilestoneLabelTarget.focus?.();
  }

  cancelAddMilestone(event) {
    if (event) event.preventDefault();
    this._resetAddMilestoneForm();
    if (this.hasAddMilestoneFormTarget) this.addMilestoneFormTarget.hidden = true;
    if (this._quote) {
      const live = (this._invoices ?? []).filter((i) => i.status !== "cancelled");
      this._renderQuoteInvoicesButtons(this._quote, live);
    }
  }

  _resetAddMilestoneForm() {
    if (this.hasAddMilestoneLabelTarget) this.addMilestoneLabelTarget.value = "";
    if (this.hasAddMilestonePercentTarget) this.addMilestonePercentTarget.value = "";
    if (this.hasAddMilestoneAmountTarget) this.addMilestoneAmountTarget.value = "";
    if (this.hasAddMilestoneErrorTarget) this.addMilestoneErrorTarget.hidden = true;
    if (this.hasAddMilestoneSubmitTarget) this.addMilestoneSubmitTarget.disabled = false;
  }

  onAddMilestonePercentInput(event) {
    const v = Number(event.target.value);
    const ttc = Number(this._quote?.amountInclVat) || 0;
    if (!this.hasAddMilestoneAmountTarget) return;
    if (!Number.isFinite(v) || ttc <= 0 || event.target.value === "") {
      this.addMilestoneAmountTarget.value = "";
      return;
    }
    this.addMilestoneAmountTarget.value = String(Math.round((ttc * v) / 100 * 100) / 100);
  }

  onAddMilestoneAmountInput(event) {
    const v = Number(event.target.value);
    const ttc = Number(this._quote?.amountInclVat) || 0;
    if (!this.hasAddMilestonePercentTarget) return;
    if (!Number.isFinite(v) || ttc <= 0 || event.target.value === "") {
      this.addMilestonePercentTarget.value = "";
      return;
    }
    this.addMilestonePercentTarget.value = String(Math.round((v / ttc) * 10000) / 100);
  }

  async submitAddMilestone(event) {
    if (event) event.preventDefault();
    if (this.hasAddMilestoneErrorTarget) this.addMilestoneErrorTarget.hidden = true;
    const label = this.hasAddMilestoneLabelTarget
      ? (this.addMilestoneLabelTarget.value || "").trim()
      : "";
    const amountRaw = this.hasAddMilestoneAmountTarget ? this.addMilestoneAmountTarget.value : "";
    const percentRaw = this.hasAddMilestonePercentTarget ? this.addMilestonePercentTarget.value : "";
    const amount = amountRaw === "" ? null : Number(amountRaw);
    const percent = percentRaw === "" ? null : Number(percentRaw);

    if (!Number.isFinite(amount) && !Number.isFinite(percent)) {
      this._addMilestoneFieldError("Saisissez un pourcentage ou un montant.");
      return;
    }
    if (Number.isFinite(percent) && (percent <= 0 || percent > 100)) {
      this._addMilestoneFieldError("Le pourcentage doit être entre 0,01 et 100.");
      return;
    }
    if (Number.isFinite(amount) && amount <= 0) {
      this._addMilestoneFieldError("Le montant doit être strictement positif.");
      return;
    }

    const payload = {
      // Cohérence avec le wizard de création (cf. devis-facturer_controller
      // `submit()` : `m.label || "Jalon"`) — un jalon sans libellé apparaît
      // partout comme « Brouillon — Jalon » plutôt qu'avec le fallback
      // backend « X,XX % du devis F-… ».
      label: label !== "" ? label : "Jalon",
    };
    if (Number.isFinite(amount)) {
      payload.amount = amount;
    } else {
      payload.percent = percent;
    }

    if (this.hasAddMilestoneSubmitTarget) this.addMilestoneSubmitTarget.disabled = true;
    try {
      await api.addQuoteMilestone(this._quoteId, payload);
      this.showSuccess("Brouillon de jalon créé.");
      this._resetAddMilestoneForm();
      if (this.hasAddMilestoneFormTarget) this.addMilestoneFormTarget.hidden = true;
      await this.refresh();
    } catch (error) {
      const code = error instanceof APIError ? error.data?.error?.code : null;
      let msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la création du jalon.";
      if (code === "quote_overflow" || code === "invoice_overflow") {
        msg = "Ce montant porterait le total facturé au-delà du devis. Réduisez le montant ou émettez un avoir avant.";
      } else if (code === "settlement_already_issued") {
        msg = "La facture de solde a déjà été émise — aucun jalon supplémentaire n'est possible.";
      }
      this._addMilestoneFieldError(msg);
      if (this.hasAddMilestoneSubmitTarget) this.addMilestoneSubmitTarget.disabled = false;
    }
  }

  _addMilestoneFieldError(msg) {
    if (!this.hasAddMilestoneErrorTarget) return;
    this.addMilestoneErrorTarget.textContent = msg;
    this.addMilestoneErrorTarget.hidden = false;
  }

  async confirmResetDrafts(event) {
    if (event) event.preventDefault();
    if (!this._quoteId) return;
    const ok = await this.confirm(
      "Recommencer le découpage ? Tous les brouillons du lot vont être archivés et vous pourrez relancer le wizard.",
      { confirmLabel: "Recommencer", cancelLabel: "Annuler" },
    );
    if (!ok) return;
    if (this.hasInvoicesActionErrorTarget) this.invoicesActionErrorTarget.hidden = true;
    try {
      await api.resetQuoteDrafts(this._quoteId);
      this.showSuccess("Brouillons archivés.");
      window.location.assign(
        `/devis-facturer-edition.html?quoteId=${encodeURIComponent(this._quoteId)}`,
      );
    } catch (error) {
      const code = error instanceof APIError ? error.data?.error?.code : null;
      let msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la réinitialisation des brouillons.";
      if (code === "reset_blocked_invoice_issued") {
        msg = "Au moins une facture du lot est déjà émise : impossible de purger les brouillons.";
      }
      if (this.hasInvoicesActionErrorTarget) {
        this.invoicesActionErrorTarget.textContent = msg;
        this.invoicesActionErrorTarget.hidden = false;
      } else {
        this.showError(msg);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Envoyer / Renvoyer — `issued` → POST /send (transition), `sent`/`expired`
  // → POST /resend (no-op de statut).
  // ---------------------------------------------------------------------------

  async sendOrResend(event) {
    if (event) event.preventDefault();
    if (!this._quoteId) return;
    const status = this._quote?.status ?? "";
    const isResend = status === "sent" || status === "expired";
    const isInitial = status === "issued";
    if (!isResend && !isInitial) return;
    if (this.hasSendBtnTarget) this.sendBtnTarget.disabled = true;
    try {
      if (isInitial) {
        await api.sendQuote(this._quoteId, {});
        this.showSuccess("Devis envoyé au client.");
      } else {
        await api.resendQuote(this._quoteId, {});
        this.showSuccess("Devis renvoyé au client.");
      }
      await this.refresh();
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de l'envoi du devis.";
      this.showError(msg);
    } finally {
      if (this.hasSendBtnTarget) this.sendBtnTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // EPIC-056 / 56.6 — Prolonger la validité / Renouveler / Lien page client
  // ---------------------------------------------------------------------------

  openExtendValidity(event) {
    if (event) event.preventDefault();
    if (!this.hasExtendPanelTarget) return;
    this.extendPanelTarget.hidden = false;
    if (this.hasExtendDateInputTarget) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const iso = tomorrow.toISOString().slice(0, 10);
      this.extendDateInputTarget.setAttribute("min", iso);
      this.extendDateInputTarget.focus();
    }
    if (this.hasExtendErrorTarget) this.extendErrorTarget.hidden = true;
  }

  closeExtendValidity(event) {
    if (event) event.preventDefault();
    if (this.hasExtendPanelTarget) this.extendPanelTarget.hidden = true;
    if (this.hasExtendDateInputTarget) this.extendDateInputTarget.value = "";
    if (this.hasExtendErrorTarget) this.extendErrorTarget.hidden = true;
  }

  async submitExtendValidity(event) {
    if (event) event.preventDefault();
    if (!this._quoteId || !this.hasExtendDateInputTarget) return;
    const newValidityDate = this.extendDateInputTarget.value;
    if (this.hasExtendErrorTarget) this.extendErrorTarget.hidden = true;
    if (!newValidityDate) {
      this._showExtendError("Veuillez saisir une nouvelle date de validité.");
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(`${newValidityDate}T00:00:00`);
    if (!Number.isFinite(picked.getTime()) || picked <= today) {
      this._showExtendError("La nouvelle date doit être postérieure à aujourd'hui.");
      return;
    }
    if (this.hasExtendSubmitBtnTarget) this.extendSubmitBtnTarget.disabled = true;
    try {
      await api.extendQuoteValidity(this._quoteId, newValidityDate);
      this.showSuccess("La validité du devis a été prolongée. Pour le renvoyer au client, utilisez l'action « Envoyer ».");
      if (this.hasExtendPanelTarget) this.extendPanelTarget.hidden = true;
      if (this.hasExtendDateInputTarget) this.extendDateInputTarget.value = "";
      await this.refresh();
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la prolongation du devis.";
      this._showExtendError(msg);
    } finally {
      if (this.hasExtendSubmitBtnTarget) this.extendSubmitBtnTarget.disabled = false;
    }
  }

  _showExtendError(msg) {
    if (!this.hasExtendErrorTarget) return;
    this.extendErrorTarget.textContent = msg;
    this.extendErrorTarget.hidden = false;
  }

  openRenew(event) {
    if (event) event.preventDefault();
    if (this.hasRenewPanelTarget) this.renewPanelTarget.hidden = false;
    if (this.hasRenewErrorTarget) this.renewErrorTarget.hidden = true;
  }

  closeRenew(event) {
    if (event) event.preventDefault();
    if (this.hasRenewPanelTarget) this.renewPanelTarget.hidden = true;
    if (this.hasRenewErrorTarget) this.renewErrorTarget.hidden = true;
  }

  async submitRenew(event) {
    if (event) event.preventDefault();
    if (!this._quoteId) return;
    if (this.hasRenewErrorTarget) this.renewErrorTarget.hidden = true;
    if (this.hasRenewSubmitBtnTarget) this.renewSubmitBtnTarget.disabled = true;
    try {
      const resp = await api.renewQuote(this._quoteId);
      const newId = resp?.quote?.id ?? resp?.id ?? null;
      if (!newId) {
        this._showRenewError("Le brouillon a bien été créé, mais aucun identifiant n'a été renvoyé.");
        return;
      }
      window.location.assign(`/devis-edition.html?id=${encodeURIComponent(newId)}`);
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors du renouvellement du devis.";
      this._showRenewError(msg);
    } finally {
      if (this.hasRenewSubmitBtnTarget) this.renewSubmitBtnTarget.disabled = false;
    }
  }

  _showRenewError(msg) {
    if (!this.hasRenewErrorTarget) return;
    this.renewErrorTarget.textContent = msg;
    this.renewErrorTarget.hidden = false;
  }

  async copyPublicLink(event) {
    if (event) event.preventDefault();
    if (!this.hasPublicLinkUrlTarget) return;
    const url = this.publicLinkUrlTarget.textContent.trim();
    if (!url) return;
    let copied = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        copied = false;
      }
    }
    if (!copied) {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        copied = document.execCommand("copy");
      } catch {
        copied = false;
      }
      document.body.removeChild(ta);
    }
    if (copied) {
      this.showSuccess("Lien copié dans le presse-papier.");
    } else {
      this.showError("Impossible de copier le lien automatiquement. Sélectionnez et copiez-le manuellement.");
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatMoney(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
